import axios, { AxiosResponse } from 'axios'
import {
  RevisionEntityDto,
  RevisionQueryResponse,
  TelekitDispositivoDtoCreate,
  TelekitDispositivoDtoEncerramento,
  TelekitDispositivoDtoUpdate,
  TelekitDispositivoList,
  TelekitDispositivosQueryResponse,
  TelekitDtoCreate,
  TelekitDtoCustodia,
  TelekitDtoUpdate,
  TelekitsQueryResponse,
  TelekitView,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL
const TELEKITS_URL = `${API_URL}/telekits`

/**
 * Busca todos os telekits
 * @param query string
 * @returns TelekitsQueryResponse
 */
export const getTelekits = (query: string): Promise<TelekitsQueryResponse> => {
  return axios
    .get(`${TELEKITS_URL}?${query}`)
    .then((resp: AxiosResponse<TelekitsQueryResponse>) => resp.data)
}

/**
 * Busca um telekit
 * @param telekitId Identificador do telekit
 * @returns TelekitView
 */
export const getTelekitById = (telekitId: number): Promise<TelekitView> => {
  return axios
    .get(`${TELEKITS_URL}/${telekitId}`)
    .then((resp: AxiosResponse<TelekitView>) => resp.data)
}

/**
 * Altera o cadastro de um telekit
 * @param telekitId Identificador do telekit
 * @param telekit TelekitDtoUpdate
 * @returns TelekitView
 */
export const updateTelekit = (
  telekitId: number,
  telekit: TelekitDtoUpdate
): Promise<TelekitView> => {
  return axios
    .put(`${TELEKITS_URL}/${telekitId}`, telekit)
    .then((resp: AxiosResponse<TelekitView>) => resp.data)
}

/**
 * Deleta o cadastro de um telekit
 * @param telekitId Identificador do telekit
 * @returns void
 */
export const deleteTelekit = (telekitId: number): Promise<void> => {
  return axios.delete(`${TELEKITS_URL}/${telekitId}`)
}

/**
 * Ativa um telekit
 * @param telekitId Identificador do telekit
 * @returns TelekitView
 */
export const activateTelekit = (telekitId: number): Promise<TelekitView> => {
  return axios
    .patch(`${TELEKITS_URL}/${telekitId}/ativar`)
    .then((resp: AxiosResponse<TelekitView>) => resp.data)
}

/**
 * Desativa um telekit
 * @param telekitId Identificador do telekit
 * @returns TelekitView
 */
export const deactivateTelekit = (telekitId: number): Promise<TelekitView> => {
  return axios
    .patch(`${TELEKITS_URL}/${telekitId}/inativar`)
    .then((resp: AxiosResponse<TelekitView>) => resp.data)
}

/**
 * Busca as revisões de uma entidade
 * @param entidadeId Identificador da entidade
 * @param query string
 * @returns TelekitView
 */
export const getTelekitsRevisoes = (
  entidadeId: number,
  query: string
): Promise<RevisionQueryResponse> => {
  return axios
    .get(`${TELEKITS_URL}/${entidadeId}/revisoes/${query}`)
    .then((resp: AxiosResponse<RevisionQueryResponse>) => resp.data)
}

/**
 * Busca uma revisão de uma entidade
 * @param entidadeId Identificador da entidade
 * @param revisionNumber Número da revisão
 * @returns
 */
export const getRevisionByEntidade = (
  entidadeId: number,
  revisionNumber: string
): Promise<RevisionEntityDto> => {
  return axios
    .get(`${TELEKITS_URL}/${entidadeId}/revisoes/${revisionNumber}`)
    .then((resp: AxiosResponse<RevisionEntityDto>) => resp.data)
}

/**
 * Busca todos os telekits da empresa
 * @param empresaId Identificador da empresa
 * @param query string
 * @returns TelekitsQueryResponse
 */
export const getTelekitsByEmpresa = (
  empresaId: number,
  query: string
): Promise<TelekitsQueryResponse> => {
  return axios
    .get(`${TELEKITS_URL}/empresa/${empresaId}?${query}`)
    .then((resp: AxiosResponse<TelekitsQueryResponse>) => resp.data)
}

/**
 * Cadastra um telekit
 * @param empresaId Idetificador da empresa
 * @param telekit TelekitDtoCreate
 * @returns TelekitView
 */
export const createTelekit = (
  empresaId: number,
  telekit: TelekitDtoCreate
): Promise<TelekitView> => {
  return axios
    .post(`${TELEKITS_URL}/empresa/${empresaId}`, telekit)
    .then((resp: AxiosResponse<TelekitView>) => resp.data)
}

/**
 * Alocações de dispositivos da maleta (`ativo=true` para as atuais; sem `ativo`, todas).
 * @param telekitId Identificador do telekit
 * @param query string (`ativo`, `page`, `size`, `sort`)
 */
export const getAlocacoesTelekit = (
  telekitId: number,
  query: string
): Promise<TelekitDispositivosQueryResponse> =>
  axios
    .get(`${TELEKITS_URL}/${telekitId}/dispositivos?${query}`)
    .then((resp: AxiosResponse<TelekitDispositivosQueryResponse>) => resp.data)

/** Aloca um dispositivo na maleta. Se ele era de outra empresa, a custódia passa à da maleta. */
export const alocarDispositivo = (
  telekitId: number,
  dto: TelekitDispositivoDtoCreate
): Promise<TelekitDispositivoList> =>
  axios
    .post(`${TELEKITS_URL}/${telekitId}/dispositivos`, dto)
    .then((resp: AxiosResponse<TelekitDispositivoList>) => resp.data)

/** Altera AE Title, modalidade, descrição e observação de uma alocação ativa. */
export const editarAlocacao = (
  telekitId: number,
  idAlocacao: number,
  dto: Omit<TelekitDispositivoDtoUpdate, 'ativo'>
): Promise<TelekitDispositivoList> => {
  const corpo: TelekitDispositivoDtoUpdate = { ...dto, ativo: true }
  return axios
    .put(`${TELEKITS_URL}/${telekitId}/dispositivos/${idAlocacao}`, corpo)
    .then((resp: AxiosResponse<TelekitDispositivoList>) => resp.data)
}

/** Encerra a alocação. O corpo é obrigatório (`motivoFim`); sem ele o backend responde 422. */
export const desalocarDispositivo = (
  telekitId: number,
  idAlocacao: number,
  dto: TelekitDispositivoDtoEncerramento
): Promise<TelekitDispositivoList> =>
  axios
    .patch(`${TELEKITS_URL}/${telekitId}/dispositivos/${idAlocacao}/inativar`, dto)
    .then((resp: AxiosResponse<TelekitDispositivoList>) => resp.data)

/** Troca a empresa e/ou o regime da maleta; as alocações ativas acompanham. */
export const transferirCustodiaTelekit = (
  telekitId: number,
  dto: TelekitDtoCustodia
): Promise<TelekitView> =>
  axios
    .patch(`${TELEKITS_URL}/${telekitId}/custodia`, dto)
    .then((resp: AxiosResponse<TelekitView>) => resp.data)
